/**
 * Policy Fixture.
 * Spec: lmm-wiki/wiki/orbit-specs/done/policy-diagnose.md
 * § 312j BGB Pflicht-Versionsverwaltung
 */

export const DOC_TYPES = [
  { value: 'agb', label: 'AGB', description: 'Allgemeine Geschäftsbedingungen' },
  { value: 'privacy', label: 'Datenschutz', description: 'Datenschutzerklärung' },
  { value: 'withdrawal', label: 'Widerrufsbelehrung', description: 'Widerrufsrecht-Belehrung (B2C)' },
  { value: 'data_processing', label: 'Auftragsverarbeitung', description: 'AV-Vertrag (B2B)' },
];

function persist() {
  if (typeof window === 'undefined') return null;
  if (!window.__ORBIT_POLICY_DB__) {
    const now = Date.now();
    window.__ORBIT_POLICY_DB__ = [
      {
        id: 11,
        documentType: 'agb',
        version: '2026-v1',
        bodyUrl: 'https://valikey.app/legal/agb/2026-v1.pdf',
        effectiveAt: new Date(now - 30 * 86_400_000).toISOString(),
        createdAt: new Date(now - 30 * 86_400_000).toISOString(),
      },
      {
        id: 12,
        documentType: 'agb',
        version: '2025-v3',
        bodyUrl: 'https://valikey.app/legal/agb/2025-v3.pdf',
        effectiveAt: new Date(now - 365 * 86_400_000).toISOString(),
        createdAt: new Date(now - 365 * 86_400_000).toISOString(),
      },
      {
        id: 13,
        documentType: 'privacy',
        version: '2027-v1',
        bodyUrl: 'https://valikey.app/legal/privacy/2027-v1.pdf',
        // ⚠ Future-Date → triggert future_only Status in der Diagnose
        effectiveAt: new Date(now + 60 * 86_400_000).toISOString(),
        createdAt: new Date(now - 5 * 86_400_000).toISOString(),
      },
      {
        id: 14,
        documentType: 'data_processing',
        version: '2026-v1',
        bodyUrl: 'https://valikey.app/legal/av/2026-v1.pdf',
        effectiveAt: new Date(now - 90 * 86_400_000).toISOString(),
        createdAt: new Date(now - 90 * 86_400_000).toISOString(),
      },
      // 'withdrawal' fehlt absichtlich → missing Status
    ];
  }
  return window.__ORBIT_POLICY_DB__;
}

export function policyFixture() {
  const db = persist();
  // sortiert nach documentType, dann effectiveAt DESC
  const sorted = [...db].sort((a, b) => {
    if (a.documentType !== b.documentType) return a.documentType.localeCompare(b.documentType);
    return new Date(b.effectiveAt).getTime() - new Date(a.effectiveAt).getTime();
  });
  return { versions: sorted };
}

export function policyDiagnoseFixture(brandOrigin = 'valikey.app') {
  const db = persist();
  const now = new Date();
  const byDocType = {};

  DOC_TYPES.forEach((dt) => {
    const versions = db
      .filter((v) => v.documentType === dt.value)
      .sort((a, b) => new Date(b.effectiveAt).getTime() - new Date(a.effectiveAt).getTime());

    if (versions.length === 0) {
      byDocType[dt.value] = {
        status: 'missing',
        reason: `Keine Version für "${dt.label}" hinterlegt. Acceptance-Flow rejected — Customer kann nicht checken.`,
        versions: [],
      };
      return;
    }

    const inForceVersion = versions.find((v) => new Date(v.effectiveAt) <= now);

    if (!inForceVersion) {
      const next = versions[versions.length - 1];
      byDocType[dt.value] = {
        status: 'future_only',
        reason: `Alle Versionen liegen in der Zukunft. Nächste wirksam ab ${next.effectiveAt}.`,
        versions: versions.map((v) => ({ ...v, isInForce: false })),
      };
      return;
    }

    byDocType[dt.value] = {
      status: 'ok',
      reason: `Aktuelle Version "${inForceVersion.version}" wirksam seit ${inForceVersion.effectiveAt}.`,
      versions: versions.map((v) => ({ ...v, isInForce: v.id === inForceVersion.id })),
    };
  });

  return {
    brandOrigin,
    tenantId: 3,
    serverNow: now.toISOString(),
    byDocType,
  };
}

export function createPolicyVersionFixture({ documentType, version, bodyUrl, effectiveAt }) {
  const db = persist();
  // Duplicate-Check (brand, documentType, version)
  if (db.some((v) => v.documentType === documentType && v.version === version)) {
    const err = new Error(`Version "${version}" für ${documentType} existiert bereits.`);
    err.code = 'ER_DUP_ENTRY';
    err.status = 409;
    throw err;
  }
  const id = Math.max(0, ...db.map((v) => v.id)) + 1;
  const created = {
    id,
    documentType,
    version,
    bodyUrl: bodyUrl || null,
    effectiveAt: effectiveAt || new Date().toISOString(),
    createdAt: new Date().toISOString(),
  };
  db.push(created);
  return { version: created };
}

export function activatePolicyNowFixture(id) {
  const db = persist();
  const target = db.find((v) => v.id === Number(id));
  if (!target) throw new Error(`Policy-Version ${id} nicht gefunden`);
  target.effectiveAt = new Date().toISOString();
  return { ok: true, id: Number(id), effectiveAt: target.effectiveAt };
}
